import { useCallback, useEffect, useRef, useState } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { BuildingSummary, BaseComponent, BuildingModel, Building } from '../../types/protocols';
import { TimelineEvent, TimelineEventType, CustomTimelineItem, TimelineAttachment } from '../../types/timeline';
import { ProjectModel } from '../../types/projects';
import { WorkOrder, WorkOrderStatus } from '../../types/workOrders';
import { Issue } from '../../types/issues';
import { BuildingCheck, checkDisplayName, lastCompletedDate, parseBuildingCheck } from '../../Pages/BuildingChecks/buildingChecksUtils';
import { BuildingCheckTemplate } from '../../Pages/BuildingChecks/types';
import { BaseComponentImpl } from '../../types/implementations';
import { normalizeLineBreaks } from '../../Helpers/FormatUtils';
import { parseDateString } from '../../utils/timelineUtils';
import { processMaintenancePlans } from '../../utils/maintenancePlanTimeline';

/**
 * Timeline event synthesis, extracted verbatim from TimelinePage (plan D4);
 * also the source for seeding report draft snapshots at creation (plan D14).
 * Events are synthesized in memory from six sources — custom timeline items,
 * work orders (split by source), inspections, component replacements,
 * projects and maintenance plan activations — and are not stored anywhere.
 */

// Helper function to create a work order completed event
const createWorkOrderCompletedEvent = (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    workOrderKey: string,
    workOrder: WorkOrder,
    projectTitlesByKey: { [key: string]: string },
    allIssues?: { [key: string]: Issue }
): TimelineEvent | null => {
    // Only completed work orders belong on the timeline (not open/cancelled).
    if (workOrder.status !== WorkOrderStatus.COMPLETED) return null;

    // Split completed work orders by what they stem from. Issue reports take
    // precedence over maintenance; anything else is "other" (manually created).
    // Mirrors the source categorisation in ArchivedWorkOrdersModal.
    let eventType: TimelineEventType;
    if (workOrder.sourceIssueKey) {
        eventType = TimelineEventType.WORK_ORDER_ISSUE;
    } else if (workOrder.maintenanceTemplateId) {
        eventType = TimelineEventType.WORK_ORDER_MAINTENANCE;
    } else {
        eventType = TimelineEventType.WORK_ORDER_OTHER;
    }

    let eventDate: Date | null = null;
    let displayDate: string = '';

    if (workOrder.completedAt) {
        // Use completion date if available (preferred)
        eventDate = parseDateString(workOrder.completedAt);
        displayDate = workOrder.completedAt;
    } else if (workOrder.createdAt) {
        // Fall back to work order creation date (closer to completion than issue report)
        eventDate = parseDateString(workOrder.createdAt);
        displayDate = workOrder.createdAt;
    } else if (workOrder.sourceIssueKey && allIssues?.[workOrder.sourceIssueKey]?.createdAt) {
        // Last resort: source issue's registration date
        const sourceIssue = allIssues[workOrder.sourceIssueKey];
        eventDate = parseDateString(sourceIssue.createdAt);
        displayDate = sourceIssue.createdAt;
    }

    if (!eventDate) return null;

    // If the work order belongs to a project, carry the project name so it can
    // be shown on the same row as the building name.
    const projectKey = workOrder.projectKey;
    const projectTitle = projectKey ? projectTitlesByKey[projectKey] : undefined;

    return {
        buildingKey,
        buildingName,
        buildingSummary,
        eventType,
        date: eventDate,
        displayDate,
        workOrderKey,
        workOrderTitle: workOrder.title,
        workOrderPriority: workOrder.priority,
        completionComment: workOrder.completionComment,
        afterPictures: workOrder.afterPictures,
        projectId: projectKey,
        projectTitle
    };
};

// Helper function to create an inspection completed event
const createInspectionCompletedEvent = (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    templateId: string,
    templateName: string,
    check: BuildingCheck
): TimelineEvent | null => {
    const completedDateStr = lastCompletedDate(check);
    if (!completedDateStr) return null;

    // lastCompletedAt may carry a time (callable); the event is dated on the day
    const completedDate = new Date(completedDateStr + ' 00:00:00');
    if (isNaN(completedDate.getTime())) return null;

    return {
        buildingKey,
        buildingName,
        buildingSummary,
        eventType: TimelineEventType.INSPECTION_COMPLETED,
        date: completedDate,
        displayDate: completedDateStr,
        inspectionTemplateId: templateId,
        inspectionTemplateName: templateName,
        inspectionMeasurement: check.lastMeasurement,
        inspectionComponentKey: check.componentKey,
        inspectionLocation: check.location
    };
};

// Helper function to create a component replaced event
const createComponentReplacedEvent = (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    componentKey: string,
    component: BaseComponent,
    replacedComponent: BaseComponent
): TimelineEvent | null => {
    // Use archivedAt from the OLD component as the event date
    if (!replacedComponent.archivedAt) return null;

    const replacedDate = parseDateString(replacedComponent.archivedAt);
    if (!replacedDate) return null;

    // Use BaseComponentImpl.displayName() for consistent component naming
    const componentImpl = new BaseComponentImpl({ ...component, key: componentKey });
    const replacedImpl = new BaseComponentImpl({ ...replacedComponent, key: component.replacedFromKey! });

    return {
        buildingKey,
        buildingName,
        buildingSummary,
        eventType: TimelineEventType.COMPONENT_REPLACED,
        date: replacedDate,
        displayDate: replacedComponent.archivedAt,
        componentKey,
        componentDisplayName: componentImpl.displayName({} as Building),
        componentTypeKey: component.type,
        replacedComponentKey: component.replacedFromKey,
        replacedComponentName: replacedImpl.displayName({} as Building)
    };
};

// Helper function to create a custom timeline event
const createCustomEvent = async (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    itemId: string,
    item: CustomTimelineItem,
    components?: { [key: string]: BaseComponent }
): Promise<TimelineEvent | null> => {
    const itemDate = parseDateString(item.date);
    if (!itemDate) return null;

    // Resolve any linked components to display names (skip keys that no longer
    // exist in the building structure).
    let linkedComponents: { key: string; displayName: string }[] | undefined;
    if (item.componentKeys && item.componentKeys.length > 0) {
        const resolved = item.componentKeys
            .map(key => {
                const component = components?.[key];
                if (!component) return null;
                const displayName = new BaseComponentImpl({ ...component, key }).displayName({} as Building);
                return { key, displayName };
            })
            .filter((c): c is { key: string; displayName: string } => c !== null);
        if (resolved.length > 0) linkedComponents = resolved;
    }

    // Load attachments if they exist
    let attachments: { [key: string]: TimelineAttachment } | undefined;
    if (item.attachments) {
        attachments = item.attachments;
    } else {
        // Try to load attachments from separate path
        const db = getDatabase();
        const attachmentsRef = ref(db, `buildings/${buildingKey}/timelineItems/${itemId}/attachments`);
        try {
            const attachmentsSnapshot = await get(attachmentsRef);
            if (attachmentsSnapshot.exists()) {
                attachments = attachmentsSnapshot.val() as { [key: string]: TimelineAttachment };
            }
        } catch (error) {
            // Attachments might not exist, that's okay
        }
    }

    return {
        buildingKey,
        buildingName,
        buildingSummary,
        eventType: TimelineEventType.CUSTOM,
        date: itemDate,
        displayDate: item.date,
        customEventId: itemId,
        title: item.title,
        // Normalize on read so existing records that contain stray break
        // characters (U+2028/U+2029, lone CR, etc.) display consistently and
        // surface the breaks as editable newlines in the edit field. Saving
        // then persists the cleaned text back to the database.
        description: normalizeLineBreaks(item.description || ''),
        inexactDate: item.inexactDate || false,
        attachments,
        linkedComponents
    };
};

// Helper function to process work orders (flat structure)
const processWorkOrders = (
    workOrders: { [key: string]: WorkOrder },
    buildingSummaries: { [key: string]: BuildingSummary },
    events: TimelineEvent[],
    allIssues: { [key: string]: Issue },
    projectTitlesByKey: { [key: string]: string },
    singleBuildingKey?: string
) => {
    Object.entries(workOrders).forEach(([key, wo]) => {
        // Validate work order data
        if (!wo || typeof wo !== 'object') return;
        if (!wo.buildingKey || typeof wo.buildingKey !== 'string') return;

        // Filter by building if single building view
        if (singleBuildingKey && wo.buildingKey !== singleBuildingKey) return;

        const summary = buildingSummaries[wo.buildingKey];
        if (!summary) return;

        const event = createWorkOrderCompletedEvent(
            wo.buildingKey,
            summary.name || 'Namnlös byggnad',
            summary,
            key,
            wo,
            projectTitlesByKey,
            allIssues
        );
        if (event) events.push(event);
    });
};

// Helper function to collect all issues from active and archived snapshots
const collectAllIssues = (
    activeIssuesData: { [key: string]: Issue } | null,
    archivedIssuesData: { [key: string]: Issue } | null
): { [key: string]: Issue } => {
    const allIssues: { [key: string]: Issue } = {};
    if (activeIssuesData) {
        Object.assign(allIssues, activeIssuesData);
    }
    if (archivedIssuesData) {
        Object.assign(allIssues, archivedIssuesData);
    }
    return allIssues;
};

// Helper function to process inspections
const processInspections = (
    inspectionsData: { [buildingId: string]: { [checkKey: string]: unknown } },
    templates: { [templateId: string]: BuildingCheckTemplate },
    buildingSummaries: { [key: string]: BuildingSummary },
    events: TimelineEvent[],
    singleBuildingKey?: string
) => {
    Object.entries(inspectionsData).forEach(([buildingId, buildingChecks]) => {
        // Filter by building if single building view
        if (singleBuildingKey && buildingId !== singleBuildingKey) return;

        const summary = buildingSummaries[buildingId];
        if (!summary) return;

        const checks = Object.entries(buildingChecks ?? {})
            .map(([checkKey, data]) => parseBuildingCheck(checkKey, data))
            .filter((check): check is BuildingCheck => check !== null);
        checks.forEach(check => {
            const template = templates[check.templateId];
            // No component names here: a component check shows its label or the template name
            const templateName = checkDisplayName(check, template?.name || 'Okänd kontroll');

            const event = createInspectionCompletedEvent(
                buildingId,
                summary.name || 'Namnlös byggnad',
                summary,
                check.templateId,
                templateName,
                check
            );
            if (event) events.push(event);
        });
    });
};

// Helper function to process components for installation and replacement events
const processComponents = (
    buildingsData: { [key: string]: BuildingModel },
    buildingSummaries: { [key: string]: BuildingSummary },
    events: TimelineEvent[],
    singleBuildingKey?: string
) => {
    Object.entries(buildingsData).forEach(([buildingKey, buildingData]) => {
        // Filter by building if single building view
        if (singleBuildingKey && buildingKey !== singleBuildingKey) return;

        const summary = buildingSummaries[buildingKey];
        if (!summary) return;

        const components = buildingData.structure?.components;
        if (!components) return;

        Object.entries(components).forEach(([componentKey, component]) => {
            const typedComponent = component as BaseComponent;

            // Create replacement event if this component replaced another
            if (typedComponent.replacedFromKey) {
                const replacedComponent = components[typedComponent.replacedFromKey] as BaseComponent | undefined;
                if (replacedComponent) {
                    const replaceEvent = createComponentReplacedEvent(
                        buildingKey,
                        summary.name || 'Namnlös byggnad',
                        summary,
                        componentKey,
                        typedComponent,
                        replacedComponent
                    );
                    if (replaceEvent) events.push(replaceEvent);
                }
            }
        });
    });
};

// Helper function to process custom timeline items
const processCustomTimelineItems = async (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    customItems: { [key: string]: CustomTimelineItem },
    events: TimelineEvent[],
    components?: { [key: string]: BaseComponent }
) => {
    const eventPromises = Object.entries(customItems).map(async ([itemId, item]) => {
        return await createCustomEvent(buildingKey, buildingName, buildingSummary, itemId, item, components);
    });
    const createdEvents = await Promise.all(eventPromises);
    createdEvents.forEach(event => {
        if (event) events.push(event);
    });
};

// Helper function to create a project event (all projects, regardless of status)
const createProjectEvent = (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    projectId: string,
    project: ProjectModel
): TimelineEvent | null => {
    // Primary date: actual completion date. Secondary (fallback): start date
    // (e.g. for projects that aren't completed yet and have no actualEndDate).
    const dateString = project.actualEndDate || project.startDate;
    if (!dateString) return null;

    const eventDate = parseDateString(dateString);
    if (!eventDate) return null;

    return {
        buildingKey,
        buildingName,
        buildingSummary,
        eventType: TimelineEventType.PROJECT_END,
        date: eventDate,
        displayDate: dateString,
        projectId,
        projectTitle: project.title,
        projectStatus: project.status
    };
};

// Helper function to process projects (all projects)
const processProjects = (
    buildingKey: string,
    buildingName: string,
    buildingSummary: BuildingSummary,
    projects: { [key: string]: ProjectModel },
    events: TimelineEvent[]
) => {
    Object.entries(projects).forEach(([projectId, project]) => {
        const projectEvent = createProjectEvent(buildingKey, buildingName, buildingSummary, projectId, project);
        if (projectEvent) events.push(projectEvent);
    });
};

/**
 * Fetch and synthesize all timeline events, for one building (buildingKey set)
 * or across all buildings. One-shot fetch; the returned events are sorted
 * newest first, exactly as TimelinePage always has.
 */
async function fetchTimelineEvents(buildingKey?: string): Promise<{
    events: TimelineEvent[];
    buildingSummary: BuildingSummary | null;
}> {
    const db = getDatabase();
    const timelineEvents: TimelineEvent[] = [];
    let singleBuildingSummary: BuildingSummary | null = null;

    if (buildingKey) {
        // Fetch single building - parallel fetch all data sources
        const [
            buildingSummarySnapshot,
            buildingSnapshot,
            projectsSnapshot,
            activeWorkOrdersSnapshot,
            archivedWorkOrdersSnapshot,
            activeIssuesSnapshot,
            archivedIssuesSnapshot,
            inspectionsActiveSnapshot,
            inspectionTemplatesSnapshot,
            activePlanSnapshot,
            archivedPlansSnapshot
        ] = await Promise.all([
            get(ref(db, `buildingsSummary/${buildingKey}`)),
            get(ref(db, `buildings/${buildingKey}`)),
            get(ref(db, 'projects')),
            get(ref(db, 'workOrders/active')),
            get(ref(db, 'workOrders/archive')),
            get(ref(db, 'issues/active')),
            get(ref(db, 'issues/archive')),
            get(ref(db, 'inspections/active')),
            get(ref(db, 'inspections/templates')),
            get(ref(db, `maintenance/active/${buildingKey}`)),
            get(ref(db, `maintenance/archive/${buildingKey}`))
        ]);

        if (!buildingSummarySnapshot.exists()) {
            throw new Error('Byggnaden hittades ej');
        }

        const building = buildingSummarySnapshot.val() as BuildingSummary;
        singleBuildingSummary = building;
        const buildingSummaries = { [buildingKey]: building };
        const buildingName = building.name || 'Namnlös byggnad';

        // Process custom timeline items and component installations
        if (buildingSnapshot.exists()) {
            const buildingData = buildingSnapshot.val();
            if (buildingData.timelineItems) {
                const customItems = buildingData.timelineItems as { [key: string]: CustomTimelineItem };
                await processCustomTimelineItems(buildingKey, buildingName, building, customItems, timelineEvents, buildingData.structure?.components);
            }
            // Process component installations (use typed BuildingModel for structure access)
            const typedBuildingData = buildingData as BuildingModel;
            processComponents({ [buildingKey]: typedBuildingData }, buildingSummaries, timelineEvents, buildingKey);
        }

        // Map projectKey -> project title, for labelling work orders
        // that belong to a project.
        const projectTitlesByKey: { [key: string]: string } = {};

        // Process projects (only completed)
        if (projectsSnapshot.exists()) {
            const allProjects = projectsSnapshot.val() as { [key: string]: ProjectModel & { buildingKey: string } };
            const buildingProjects: { [key: string]: ProjectModel } = {};
            Object.entries(allProjects).forEach(([projectKey, project]) => {
                if (project?.title) {
                    projectTitlesByKey[projectKey] = project.title;
                }
                if (project.buildingKey === buildingKey) {
                    buildingProjects[projectKey] = project;
                }
            });
            processProjects(buildingKey, buildingName, building, buildingProjects, timelineEvents);
        }

        // Collect all issues for cross-referencing work order completion dates
        const allIssues = collectAllIssues(
            activeIssuesSnapshot.exists() ? activeIssuesSnapshot.val() : null,
            archivedIssuesSnapshot.exists() ? archivedIssuesSnapshot.val() : null
        );

        // Process work orders (completed only)
        if (activeWorkOrdersSnapshot.exists()) {
            processWorkOrders(activeWorkOrdersSnapshot.val(), buildingSummaries, timelineEvents, allIssues, projectTitlesByKey, buildingKey);
        }
        if (archivedWorkOrdersSnapshot.exists()) {
            processWorkOrders(archivedWorkOrdersSnapshot.val(), buildingSummaries, timelineEvents, allIssues, projectTitlesByKey, buildingKey);
        }

        // Process inspections
        if (inspectionsActiveSnapshot.exists() && inspectionTemplatesSnapshot.exists()) {
            processInspections(
                inspectionsActiveSnapshot.val(),
                inspectionTemplatesSnapshot.val(),
                buildingSummaries,
                timelineEvents,
                buildingKey
            );
        }

        // Process maintenance plan activations
        processMaintenancePlans(
            { [buildingKey]: activePlanSnapshot.val() },
            { [buildingKey]: archivedPlansSnapshot.val() },
            buildingSummaries,
            timelineEvents
        );
    } else {
        // Fetch all buildings - parallel fetch all data sources
        const [
            buildingsSummarySnapshot,
            buildingsSnapshot,
            projectsSnapshot,
            activeWorkOrdersSnapshot,
            archivedWorkOrdersSnapshot,
            activeIssuesSnapshot,
            archivedIssuesSnapshot,
            inspectionsActiveSnapshot,
            inspectionTemplatesSnapshot,
            activePlansSnapshot,
            archivedPlansSnapshot
        ] = await Promise.all([
            get(ref(db, 'buildingsSummary')),
            get(ref(db, 'buildings')),
            get(ref(db, 'projects')),
            get(ref(db, 'workOrders/active')),
            get(ref(db, 'workOrders/archive')),
            get(ref(db, 'issues/active')),
            get(ref(db, 'issues/archive')),
            get(ref(db, 'inspections/active')),
            get(ref(db, 'inspections/templates')),
            get(ref(db, 'maintenance/active')),
            get(ref(db, 'maintenance/archive'))
        ]);

        if (buildingsSummarySnapshot.exists()) {
            const summaryData = buildingsSummarySnapshot.val();
            const buildingSummaries: { [key: string]: BuildingSummary } = {};

            Object.entries(summaryData).forEach(([key, value]: [string, any]) => {
                buildingSummaries[key] = value as BuildingSummary;
            });

            // Process custom timeline items and component installations from buildings
            if (buildingsSnapshot.exists()) {
                const buildingsData = buildingsSnapshot.val();

                const processPromises = Object.entries(buildingsData).map(async ([bKey, buildingData]: [string, any]) => {
                    const bSummary = buildingSummaries[bKey];
                    if (!bSummary) return;

                    const bName = bSummary.name || 'Namnlös byggnad';

                    if (buildingData.timelineItems) {
                        const customItems = buildingData.timelineItems as { [key: string]: CustomTimelineItem };
                        await processCustomTimelineItems(bKey, bName, bSummary, customItems, timelineEvents, buildingData.structure?.components);
                    }
                });
                await Promise.all(processPromises);

                // Process component installations (cast to typed model for structure access)
                processComponents(buildingsData as { [key: string]: BuildingModel }, buildingSummaries, timelineEvents);
            }

            // Map projectKey -> project title, for labelling work orders
            // that belong to a project.
            const projectTitlesByKey: { [key: string]: string } = {};

            // Process projects (only completed)
            if (projectsSnapshot.exists()) {
                const allProjects = projectsSnapshot.val() as { [key: string]: ProjectModel & { buildingKey: string } };
                Object.entries(allProjects).forEach(([projectKey, project]) => {
                    if (project?.title) {
                        projectTitlesByKey[projectKey] = project.title;
                    }
                    const bSummary = buildingSummaries[project.buildingKey];
                    if (!bSummary) return;
                    const bName = bSummary.name || 'Namnlös byggnad';
                    processProjects(project.buildingKey, bName, bSummary, { [projectKey]: project }, timelineEvents);
                });
            }

            // Collect all issues for cross-referencing work order completion dates
            const allIssues = collectAllIssues(
                activeIssuesSnapshot.exists() ? activeIssuesSnapshot.val() : null,
                archivedIssuesSnapshot.exists() ? archivedIssuesSnapshot.val() : null
            );

            // Process work orders (completed only)
            if (activeWorkOrdersSnapshot.exists()) {
                processWorkOrders(activeWorkOrdersSnapshot.val(), buildingSummaries, timelineEvents, allIssues, projectTitlesByKey);
            }
            if (archivedWorkOrdersSnapshot.exists()) {
                processWorkOrders(archivedWorkOrdersSnapshot.val(), buildingSummaries, timelineEvents, allIssues, projectTitlesByKey);
            }

            // Process inspections
            if (inspectionsActiveSnapshot.exists() && inspectionTemplatesSnapshot.exists()) {
                processInspections(
                    inspectionsActiveSnapshot.val(),
                    inspectionTemplatesSnapshot.val(),
                    buildingSummaries,
                    timelineEvents
                );
            }

            // Process maintenance plan activations
            processMaintenancePlans(activePlansSnapshot.val(), archivedPlansSnapshot.val(), buildingSummaries, timelineEvents);
        }
    }

    // Sort by date (newest first)
    timelineEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
    return { events: timelineEvents, buildingSummary: singleBuildingSummary };
}

export interface UseTimelineEventsResult {
    /** All synthesized events, newest first. */
    allEvents: TimelineEvent[];
    /** The building's summary in single-building mode, otherwise null. */
    buildingSummary: BuildingSummary | null;
    loading: boolean;
    error: string | null;
    /** Refetch. `silent: true` keeps the current data visible while reloading. */
    reload: (options?: { silent?: boolean }) => void;
}

export function useTimelineEvents(buildingKey?: string): UseTimelineEventsResult {
    const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // Bumped by reload() to re-run the fetch effect
    const [refreshKey, setRefreshKey] = useState<number>(0);
    // When true, the next fetch refreshes data without showing the full-page
    // loading state, so an in-place refresh doesn't blank the timeline.
    const silentRefreshRef = useRef<boolean>(false);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const silent = silentRefreshRef.current;
                silentRefreshRef.current = false;
                if (!silent) {
                    setLoading(true);
                }
                setError(null);

                const { events, buildingSummary: summary } = await fetchTimelineEvents(buildingKey);
                if (cancelled) return;
                setBuildingSummary(summary);
                setAllEvents(events);
            } catch (err) {
                if (cancelled) return;
                if (err instanceof Error && err.message === 'Byggnaden hittades ej') {
                    setError(err.message);
                } else {
                    console.error('Failed to load building data', err);
                    setError('Det gick inte att ladda byggnadsdata. Försök igen senare.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, [buildingKey, refreshKey]);

    const reload = useCallback((options?: { silent?: boolean }) => {
        if (options?.silent) {
            silentRefreshRef.current = true;
        }
        setRefreshKey(k => k + 1);
    }, []);

    return { allEvents, buildingSummary, loading, error, reload };
}
